import { AuthMetadata } from '@repo/frontend-types';
import { modelURL, ModelURLSource, secureFileURL } from '@repo/front-end-utils';
import Config from '../../common/Config';
import { useUserStore } from '@repo/front-end-state';

// The address rule and the per-load cache-bust live in @repo/front-end-utils (modelURL.ts).
// The environment token is read HERE, in app code, so CRA inlines it at build time.
const modelURLSource = (authMetadata: AuthMetadata | null): ModelURLSource => ({
  baseURL: Config.modelBaseURL,
  contextToken: authMetadata?.tokens?.modelUrlKey,
  fallbackToken: process.env.REACT_APP_MODEL_URL_TOKEN,
});

export const getSecureFileURL = (path: string, authMetadata: AuthMetadata | null) =>
  secureFileURL(path, modelURLSource(authMetadata));

export function useSecureFileURL(path: string = '') {
  const { authMetadata } = useUserStore();

  const url = getSecureFileURL(path, authMetadata);

  return url;
}

// Stable within a page load, new after a full refresh (see MODEL_URL_SESSION_CACHE_BUST)
export const getModelURL = (
  modelFileName: string | null | undefined,
  authMetadata: AuthMetadata | null,
  useCacheBust = true,
) => modelURL(modelFileName, modelURLSource(authMetadata), { cacheBust: useCacheBust });

export const useSecureModelURL = (modelFileName?: string | null, useCacheBust = true) => {
  // Hooks must be called unconditionally
  const { authMetadata } = useUserStore();

  return getModelURL(modelFileName, authMetadata, useCacheBust);
};

/** The model preloader's resolver: a machine's own address, with the token read at call time. */
export const preloadModelURL = (modelFileName: string) =>
  getModelURL(modelFileName, useUserStore.getState().authMetadata);
